/**
 * The full map's camera: a north-up view of the borough that zooms and pans (pinch / drag on touch, wheel /
 * drag on a desktop, the +/− and "centre on me" buttons). Pure maths, no DOM — unit-tested in mapView.test.ts.
 *
 * Units: `zoom` is CSS px per metre; (cx, cz) is the world point at the middle of the view; the view is
 * `w` x `h` CSS px. screen x = w/2 + (worldX - cx) * zoom (north up: +z runs down the screen).
 */

export interface WorldBounds {
  minX: number;
  minZ: number;
  maxX: number;
  maxZ: number;
}

/** The closest the map zooms in (CSS px per metre): ~70 m of street across a 343 px phone. */
export const MAP_MAX_ZOOM = 5;
/** Opening zoom as a multiple of "the whole borough fits". */
const OPEN_ZOOM_FACTOR = 2.4;
/** How far past the borough's edge the view may drift (CSS px), so a corner marker clears the buttons. */
const SLACK_PX = 56;

export class MapCamera {
  cx = 0;
  cz = 0;
  zoom = 1;
  w = 1;
  h = 1;
  /** Eased goal for the buttons / "centre on me" (null = none). */
  private goal: { cx: number; cz: number; zoom: number } | null = null;

  constructor(readonly bounds: WorldBounds) {
    this.cx = (bounds.minX + bounds.maxX) / 2;
    this.cz = (bounds.minZ + bounds.maxZ) / 2;
  }

  /** "The whole borough fits" — the furthest out the map goes. */
  get minZoom(): number {
    const b = this.bounds;
    return Math.min(this.w / (b.maxX - b.minX), this.h / (b.maxZ - b.minZ));
  }

  get maxZoom(): number {
    return Math.max(MAP_MAX_ZOOM, this.minZoom);
  }

  /** The view's size changed (rotation, resize): keep the centre, re-clamp. */
  resize(w: number, h: number): void {
    this.w = Math.max(1, w);
    this.h = Math.max(1, h);
    this.zoom = this.clampZoom(this.zoom);
    this.clampCentre();
  }

  clampZoom(z: number): number {
    return Math.min(this.maxZoom, Math.max(this.minZoom, z));
  }

  /** Keep the borough on screen: centred on an axis that fits, else no more than SLACK_PX past its edge. */
  clampCentre(): void {
    const b = this.bounds;
    this.cx = this.clampAxis(this.cx, b.minX, b.maxX, this.w);
    this.cz = this.clampAxis(this.cz, b.minZ, b.maxZ, this.h);
  }

  private clampAxis(c: number, lo: number, hi: number, span: number): number {
    const half = span / 2 / this.zoom;
    const slack = SLACK_PX / this.zoom;
    if (hi - lo <= 2 * half) return (lo + hi) / 2;
    return Math.min(hi - half + slack, Math.max(lo + half - slack, c));
  }

  /** Open on the player at a street-reading zoom. */
  openOn(x: number, z: number): void {
    this.goal = null;
    this.zoom = this.clampZoom(this.minZoom * OPEN_ZOOM_FACTOR);
    this.cx = x;
    this.cz = z;
    this.clampCentre();
  }

  toScreenX(x: number): number {
    return this.w / 2 + (x - this.cx) * this.zoom;
  }

  toScreenY(z: number): number {
    return this.h / 2 + (z - this.cz) * this.zoom;
  }

  toWorldX(sx: number): number {
    return this.cx + (sx - this.w / 2) / this.zoom;
  }

  toWorldZ(sy: number): number {
    return this.cz + (sy - this.h / 2) / this.zoom;
  }

  /** Zoom by `factor`, keeping the world point under (sx, sy) where it is on screen. */
  zoomAt(factor: number, sx: number, sy: number): void {
    this.goal = null;
    const wx = this.toWorldX(sx);
    const wz = this.toWorldZ(sy);
    this.zoom = this.clampZoom(this.zoom * factor);
    this.cx = wx - (sx - this.w / 2) / this.zoom;
    this.cz = wz - (sy - this.h / 2) / this.zoom;
    this.clampCentre();
  }

  /** Drag by (dx, dy) CSS px: the map follows the finger. */
  panBy(dx: number, dy: number): void {
    this.goal = null;
    this.cx -= dx / this.zoom;
    this.cz -= dy / this.zoom;
    this.clampCentre();
  }

  /**
   * Two-finger pinch: the world point that was under the fingers' midpoint at the start stays under the
   * current midpoint, and the zoom follows the fingers' spread. `start` comes from `pinchStart`.
   */
  pinch(start: PinchStart, midX: number, midY: number, dist: number): void {
    this.goal = null;
    this.zoom = this.clampZoom(start.zoom * (dist / Math.max(1, start.dist)));
    this.cx = start.wx - (midX - this.w / 2) / this.zoom;
    this.cz = start.wz - (midY - this.h / 2) / this.zoom;
    this.clampCentre();
  }

  pinchStart(midX: number, midY: number, dist: number, out: PinchStart): PinchStart {
    out.wx = this.toWorldX(midX);
    out.wz = this.toWorldZ(midY);
    out.zoom = this.zoom;
    out.dist = dist;
    return out;
  }

  /** Ease towards a centre / zoom (the buttons). `zoom` omitted = keep the current one. */
  glideTo(x: number, z: number, zoom = this.goal?.zoom ?? this.zoom): void {
    this.setGoal(x, z, this.clampZoom(zoom));
  }

  /** The +/− buttons: a smooth step about the middle of the view. */
  glideZoom(factor: number): void {
    this.setGoal(this.goal?.cx ?? this.cx, this.goal?.cz ?? this.cz, this.clampZoom((this.goal?.zoom ?? this.zoom) * factor));
  }

  /** A goal the view can actually reach (clamped as the view would be at that zoom), so a glide ends. */
  private setGoal(x: number, z: number, zoom: number): void {
    const b = this.bounds;
    const z0 = this.zoom;
    this.zoom = zoom;
    const cx = this.clampAxis(x, b.minX, b.maxX, this.w);
    const cz = this.clampAxis(z, b.minZ, b.maxZ, this.h);
    this.zoom = z0;
    this.goal = { cx, cz, zoom };
  }

  get gliding(): boolean {
    return this.goal !== null;
  }

  /** Advance the glide; true while the view moved this step. */
  step(dt: number): boolean {
    const g = this.goal;
    if (!g) return false;
    const a = 1 - Math.exp(-12 * dt);
    this.zoom = Math.exp(Math.log(this.zoom) + (Math.log(g.zoom) - Math.log(this.zoom)) * a);
    this.cx += (g.cx - this.cx) * a;
    this.cz += (g.cz - this.cz) * a;
    const done = Math.abs(this.zoom / g.zoom - 1) < 0.002 && Math.abs(g.cx - this.cx) * this.zoom < 0.5 && Math.abs(g.cz - this.cz) * this.zoom < 0.5;
    if (done) {
      this.zoom = g.zoom;
      this.cx = g.cx;
      this.cz = g.cz;
      this.goal = null;
    }
    this.clampCentre();
    return true;
  }
}

export interface PinchStart {
  wx: number;
  wz: number;
  zoom: number;
  dist: number;
}

/** A place on the map a tap can land on: a person with work, a shop, the objective. */
export interface TapCandidate {
  x: number;
  z: number;
  label: string;
}

export type TapResult = { kind: 'clear' } | { kind: 'mark'; x: number; z: number; label: string; snapped: boolean };

/** How close (CSS px) a tap must land to a marker to take it (a fingertip, not a mouse pointer). */
export const TAP_RADIUS_PX = 24;

/**
 * What a tap on the map means: on your own mark it rubs it out; on (or near) a place it marks that place by
 * name; anywhere else it marks the spot, named by `spotLabel` (the street). Off the borough: nothing.
 */
export function pickTap(
  cam: MapCamera,
  sx: number,
  sy: number,
  candidates: readonly TapCandidate[],
  mark: { x: number; z: number } | null,
  spotLabel: (x: number, z: number) => string,
): TapResult | null {
  const r2 = TAP_RADIUS_PX * TAP_RADIUS_PX;
  if (mark) {
    const dx = cam.toScreenX(mark.x) - sx;
    const dy = cam.toScreenY(mark.z) - sy;
    if (dx * dx + dy * dy <= r2) return { kind: 'clear' };
  }
  let best: TapCandidate | null = null;
  let bd = r2;
  for (const c of candidates) {
    const dx = cam.toScreenX(c.x) - sx;
    const dy = cam.toScreenY(c.z) - sy;
    const d = dx * dx + dy * dy;
    if (d <= bd) {
      bd = d;
      best = c;
    }
  }
  if (best) return { kind: 'mark', x: best.x, z: best.z, label: best.label, snapped: true };
  const x = cam.toWorldX(sx);
  const z = cam.toWorldZ(sy);
  const b = cam.bounds;
  if (x < b.minX || x > b.maxX || z < b.minZ || z > b.maxZ) return null;
  return { kind: 'mark', x, z, label: spotLabel(x, z), snapped: false };
}
